import { Component } from "react";
import { type RouteComponentProps } from "react-router";

import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { selectJobs } from "utilities/jobPicker/jobPicker";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { type IJobsitesRequired } from "commonComponents/helpers/JobsitesRequired/JobsitesRequired";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import ContactInfoButton from "commonComponents/utilities/HeaderInfo/ContactInfoButton/ContactInfoButton";
import { type IHeaderInfoResponse } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo.api.types";
import {
    type IJobPickerHandler,
    JobPickerHandler,
} from "commonComponents/utilities/JobPicker/JobPicker.api.handler";
import {
    type IMainNavigationHandler,
    MainNavigationHandler,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.handler";

import "./OwnerHeaderInfo.less";

/**
 * CSS custom property name written by `OwnerHeaderInfoInternal` with its current
 * rendered height (in px). Layout shells like `Frame` / `BTListLayoutBDS` subtract
 * this from `100vh` so full-viewport pages on the owner/client portal don't extend
 * below the visible area. Stays at `0px` when `OwnerHeaderInfo` isn't mounted.
 */
const OWNER_HEADER_INFO_HEIGHT_VAR = "--main-nav-owner-header-info-height";

interface IOwnerHeaderInfoProps extends RouteComponentProps, IJobsitesRequired {
    className?: string;
    handler?: IMainNavigationHandler;
    jobPickerHandler?: IJobPickerHandler;
    builderId: number;
    jobId: number;
    loadNewJob: boolean;
    setSelectedJobId: (jobId: number, orgId?: number) => Promise<void>;
}

interface IOwnerHeaderInfoInternalProps extends IOwnerHeaderInfoProps {
    isSpa: boolean | null;
    builderInfo: BuilderInfo;
}

interface IOwnerHeaderInfoState {
    data: IHeaderInfoResponse;
    jobChanging: boolean;
}

class OwnerHeaderInfoInternal extends Component<
    IOwnerHeaderInfoInternalProps,
    IOwnerHeaderInfoState
> {
    private mounted = true;
    private resizeObserver: ResizeObserver | null = null;
    static defaultProps = {
        handler: new MainNavigationHandler(),
        jobPickerHandler: new JobPickerHandler(),
    };

    componentWillUnmount = async () => {
        this.mounted = false;
        // Tear down the height observer and reset the CSS variable so
        // non-owner-portal pages mounted next don't carry a phantom offset.
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        document.documentElement.style.setProperty(OWNER_HEADER_INFO_HEIGHT_VAR, "0px");
    };

    /**
     * Returns the visual footprint of the header in px, including any bottom
     * margin on its last child that collapses out of the OwnerHeader box.
     * The OwnerHeader has no vertical padding/border to contain that margin,
     * so `clientHeight` / `contentRect.height` alone undershoot by ~10px.
     */
    private static measureFootprint(node: HTMLElement): number {
        let height = node.getBoundingClientRect().height;
        const lastChild = node.lastElementChild;
        if (lastChild instanceof HTMLElement) {
            const marginBottom = parseFloat(window.getComputedStyle(lastChild).marginBottom);
            if (!isNaN(marginBottom)) {
                height += marginBottom;
            }
        }
        return height;
    }

    /**
     * Callback ref that observes the root element's rendered height and writes
     * it to the CSS variable consumed by `Frame` / `BTListLayoutBDS`. Uses a
     * ResizeObserver because the header collapses to a smaller height below the
     * `screen-md` breakpoint, and we need that to flow through automatically.
     */
    private onRootRefChange = (node: HTMLDivElement | null) => {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        if (!node || typeof ResizeObserver === "undefined") {
            return;
        }

        // Set initial value synchronously so the first paint already accounts for
        // the header height — avoids a one-frame layout jump.
        document.documentElement.style.setProperty(
            OWNER_HEADER_INFO_HEIGHT_VAR,
            `${OwnerHeaderInfoInternal.measureFootprint(node)}px`,
        );

        this.resizeObserver = new ResizeObserver(([entry]) => {
            if (!entry) {
                return;
            }
            document.documentElement.style.setProperty(
                OWNER_HEADER_INFO_HEIGHT_VAR,
                `${OwnerHeaderInfoInternal.measureFootprint(entry.target as HTMLElement)}px`,
            );
        });
        this.resizeObserver.observe(node);
    };

    componentDidMount = async () => {
        // Load the info we have in local storage
        const headerInfo = BTLocalStorage.get("bt-object-headerInfo");
        if (headerInfo && headerInfo.builderId === this.props.builderId) {
            this.setState({ data: headerInfo });
        }

        // If we just picked a new job on owner portal, we want to prevent any
        // useless API calls from going out before page refresh
        if (!this.props.loadNewJob) {
            return;
        }

        try {
            const resp = await this.props.handler!.getHeader({
                selectedJobIds: [this.props.jobId],
            });
            if (this.mounted) {
                this.setState({ data: resp });

                // Store the API response in local storage
                BTLocalStorage.set("bt-object-headerInfo", resp);
            }
        } catch (e: any) {
            if (
                (e as DOMException) &&
                e.code === e.NETWORK_ERR &&
                e.message.includes("NetworkError when attempting to fetch resource.")
            ) {
                // Ignoring error per arch request. FF bug -- https://bugzilla.mozilla.org/show_bug.cgi?id=1280189
            } else {
                showAPIErrorMessage(e);
            }
        }
    };

    // This is needed for webforms, and will need to be removed when in SPA
    private onJobChanged = async (id: number) => {
        if (this.props.isSpa) {
            // move this logic up to the list page layout
            await this.props.setSelectedJobId(id);
        } else {
            try {
                this.setState({ jobChanging: true });
                selectJobs([id], false);
                await this.props.jobPickerHandler!.switchOwner(id);
                // eslint-disable-next-line no-restricted-syntax
                const loc = (window as any).location;

                // refresh page without QS (or post values)
                const url = loc.href.split("?")[0];
                // eslint-disable-next-line no-restricted-syntax
                (window as any).location = url;
            } catch (e) {
                showAPIErrorMessage(e);
                this.setState({ jobChanging: false });
            }
        }
    };

    render() {
        if (!this.state || !this.state.data) {
            return <></>;
        }

        const { data } = this.state;
        const hasLinkedJobs = data.linkedJobs.jobs.length > 1;

        return (
            <div ref={this.onRootRefChange} className={cn("OwnerHeader", this.props.className)}>
                <div className="HeaderInfo">
                    {data.logoPath && (
                        <div
                            className="BuilderLogo"
                            style={{ backgroundImage: `url(${data.logoPath})` }}
                        />
                    )}
                    <div className="OwnerJobPicker">
                        {hasLinkedJobs && (
                            <div className="OwnerJobPickerFormItem">
                                <div>
                                    <strong>Job</strong>
                                </div>
                                <BTSelect
                                    id="ownerJobPicker"
                                    data-testid="ownerJobPicker"
                                    value={this.props.jobId}
                                    treeData={data.linkedJobs.jobs.map(
                                        (j) =>
                                            new BTSelectItem({
                                                name: j.jobName,
                                                id: j.id,
                                                selected: j.id === this.props.jobId,
                                            }),
                                    )}
                                    onChange={(_field, value, _selectedItem) =>
                                        this.onJobChanged(value)
                                    }
                                    disabled={data.linkedJobs.disabled}
                                    onBlur={() => {}}
                                />
                            </div>
                        )}
                    </div>
                    {this.state.jobChanging && <BTLoading />}
                    {data.builderName !== null && (
                        <div className={hasLinkedJobs ? "margin-left-lg" : "HeaderContactUs"}>
                            <ContactInfoButton
                                jobId={this.props.jobId}
                                history={this.props.history}
                                match={this.props.match}
                                location={this.props.location}
                                parentUrl={this.props.match.url}
                            >
                                Contact Us
                            </ContactInfoButton>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export const OwnerHeaderInfo = (props: IOwnerHeaderInfoProps) => {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    return (
        <SpaInfoContext.Consumer>
            {(spaInfo) => (
                <OwnerHeaderInfoInternal
                    {...props}
                    isSpa={spaInfo.isSpa}
                    builderInfo={builderInfo}
                />
            )}
        </SpaInfoContext.Consumer>
    );
};

export default OwnerHeaderInfo;
